<script setup lang="ts">
import { useQuery, useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { useAuthStore } from "@/stores/auth";
import { useRoute } from "vue-router";
import { computed } from "vue";

const authStore = useAuthStore();
const route = useRoute();

const { loading, error, result } = useQuery(
  gql`
    query myNotifications($after: String, $first: Int) {
      myNotifications(after: $after, first: $first) {
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
          broadcastMessageId
          userId
          wasViewed
          title
          body
          createdAt
        }
      }
    }
  `,
  {},
  {
    fetchPolicy: "no-cache",
  }
);

const notifications = useResult(result, null, (data) => {
  return data.myNotifications;
});

const numberNewNotification2 = computed(() => {
  if (!notifications?.value?.nodes) {
    return 0;
  }
  const newNotifications = (notifications.value.nodes as any[]).filter(
    (node) => !node.wasViewed
  );
  return newNotifications.length;
});
</script>

<template>
  <ul class="navigation-links">
    <li v-if="route.fullPath !== '/'">
      <router-link to="/">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          fill="currentColor"
          class="bi bi-house"
          viewBox="0 0 16 16"
        >
          <path
            fill-rule="evenodd"
            d="M2 13.5V7h1v6.5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V7h1v6.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5zm11-11V6l-2-2V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5z"
          />
          <path
            fill-rule="evenodd"
            d="M7.293 1.5a1 1 0 0 1 1.414 0l6.647 6.646a.5.5 0 0 1-.708.708L8 2.207 1.354 8.854a.5.5 0 1 1-.708-.708L7.293 1.5z"
          />
        </svg>
      </router-link>
    </li>
    <li class="spacer"></li>
    <li>
      <span class="label-only">
        <span class="icon-with-badge">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            fill="currentColor"
            class="bi bi-bell"
            viewBox="0 0 16 16"
          >
            <path
              d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z"
            />
          </svg>
          <span v-if="numberNewNotification2 >= 10" class="badge">9+</span>
          <span v-else-if="numberNewNotification2 <= 0"></span>
          <span v-else class="badge">{{ numberNewNotification2 }}</span>
        </span>

        <ul v-if="loading" class="right">
          <li>
            <router-link to=""> Carregando... </router-link>
          </li>
          <li class="divider"></li>
          <li>
            <router-link to="/notifications"> Ver todas </router-link>
          </li>
        </ul>

        <ul v-else-if="notifications && notifications.length" class="right">
          <li v-for="notification of notifications">
            <router-link to="">
              {{ notification.title }}
            </router-link>
          </li>
          <li class="divider"></li>
          <li>
            <router-link to="/notifications"> Ver todas </router-link>
          </li>
        </ul>

        <ul v-else class="right">
          <li>
            <router-link to=""> Não há notificações </router-link>
          </li>
          <li class="divider"></li>
          <li>
            <router-link to="/notifications"> Ver todas </router-link>
          </li>
        </ul>
      </span>
    </li>
    <li>
      <span class="label-only">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25"
          height="25"
          fill="currentColor"
          class="bi bi-person-circle"
          viewBox="0 0 16 16"
        >
          <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
          <path
            fill-rule="evenodd"
            d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"
          />
        </svg>
        <span class="d-md-none">Usuário</span>
      </span>
      <ul class="right">
        <li>
          <span class="label-only">{{ authStore.name }}</span>
        </li>
        <li class="divider"></li>
        <li v-if="authStore.isAdmin">
          <router-link to="/adm/users"> Usuários </router-link>
        </li>
        <li v-if="authStore.isAdmin">
          <router-link to="/adm/account-deletion-requests"> Exclusões de Conta </router-link>
        </li>
        <li v-if="authStore.isAdmin" class="divider"></li>
        <li>
          <router-link to="/user/my-personal-data"> Meus dados </router-link>
        </li>
        <li>
          <router-link to="/user/subscriptions"> Minha assinatura </router-link>
        </li>
        <li>
          <router-link to="/auth/logout"> Sair </router-link>
        </li>
      </ul>
    </li>
  </ul>
</template>

<style scoped>
.icon-with-badge {
  font-size: 24px;
}
</style>
